// GtClientsTable — the Clients centre screen: schema-driven table + filter rail +
// saved views + column chooser + CSV export. Mirrors GtRolesTable (built 22 Jul),
// server-driven through /api/filter/clients. Reading 1: client = buyer. "Add client"
// with dedupe is the primary action. GtSelect for all dropdowns (house rule).
import { useState, useEffect, useCallback, useRef } from 'react';
import API_BASE from '../../config';
import GtTable from '../../components/GtTable';
import { SearchHint, splitSearch, searchCondition, searchSort } from '../../components/gtSearch'; // C1-SEARCH
import GtPager from '../../components/GtPager';
import GtCombo from '../../components/GtCombo';
import { GtSelect } from '../../components/GtControls';
import { CLIENT_COLUMNS, ALL_CLIENT_COLUMNS } from '../../components/gtClientColumns';
import GtFilterRail from '../../components/GtFilterRail';
import { CLIENT_SCHEMA } from '../../components/gtClientFilterSchema';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import GtClientRecord from './GtClientRecord';
import { paths } from '../../components/gtRoutes';
import GtImportWizard from '../../components/GtImportWizard'; // C1-CLIENTIMPORT
import gtConfirm from '../../components/GtConfirm'; // C1-NEWS

const STATUS_OPTIONS = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'active', label: 'Active' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'archived', label: 'Archived' },
];

export default function GtClientsTable({ getToken, userName, userImage }) {
  // Open client and any nested open role both come from the URL.
  const { clientId, roleId, candidateId } = useParams();
  const navigate = useNavigate();
  const openId = clientId || null;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchMode, setSearchMode] = useState('auto'); // C1-SEARCH
  const [searchInfo, setSearchInfo] = useState(null);
  const routeLoc = useLocation();
  // ?q= from search everywhere ("See all ... in the list") fills the box once.
  useEffect(() => {
    const sp = new URLSearchParams(routeLoc.search);
    const v = sp.get('q');
    if (v == null) return;
    setQuery(v); setDebouncedQuery(v.trim()); setSearchMode('auto');
    sp.delete('q');
    navigate({ pathname: routeLoc.pathname, search: sp.toString() ? '?' + sp.toString() : '' }, { replace: true });
  }, [routeLoc.search]); // eslint-disable-line react-hooks/exhaustive-deps
  // A saved view can hold a search: it goes back into the box, the rest into the rail.
  function applyViewTree(t) {
    const s = splitSearch(t);
    setFilterTree(s.tree); setQuery(s.q); setDebouncedQuery(s.q.trim()); setSearchMode(s.mode);
  }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState(() => CLIENT_COLUMNS.map(c => c.key));
  const [draftKeys, setDraftKeys] = useState(() => CLIENT_COLUMNS.map(c => c.key));
  const [dragKey, setDragKey] = useState(null);

  const [filterTree, setFilterTree] = useState(null);
  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [distincts, setDistincts] = useState({});

  const [showNew, setShowNew] = useState(false);
  const [importing, setImporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ name: '', website: '', sector: '', size: '', location: '', status: 'prospect', brief: '' });
  const [sectors, setSectors] = useState([]);

  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, { ...opts, headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { const err = new Error(body.detail || body.error || ('request failed: ' + res.status)); err.body = body; err.status = res.status; throw err; }
    return body;
  }, [getToken]);

  useEffect(() => { if (showColumns) setDraftKeys(visibleKeys); }, [showColumns]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sector is a curated list rather than a free text box, so the same company
  // does not arrive as "Fintech", "fintech" and "Fin-tech" across three records.
  // It stays free-entry because the column is a plain VARCHAR and the list
  // cannot anticipate every niche.
  useEffect(() => {
    if (!showNew) return undefined;
    let cancelled = false;
    authFetch('/api/sectors')
      .then(r => { if (!cancelled) setSectors((r && r.sectors) || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showNew, authFetch]);
  useEffect(() => { const t = setTimeout(() => setDebouncedQuery(query.trim()), 250); return () => clearTimeout(t); }, [query]);

  const filterActive = !!(filterTree && Array.isArray(filterTree.and) && filterTree.and.length > 0);

  const effectiveTree = useCallback(() => {
    const parts = [];
    if (filterTree && Array.isArray(filterTree.and)) parts.push(...filterTree.and);
    const sc = searchCondition(debouncedQuery, searchMode); // C1-SEARCH: the whole record, typos, Boolean
    if (sc) parts.push(sc);
    return parts.length ? { and: parts } : null;
  }, [filterTree, debouncedQuery, searchMode]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await authFetch('/api/filter/clients', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tree: effectiveTree(), sort: searchSort(sort, debouncedQuery), limit: pageSize, offset: page * pageSize }),
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setSearchInfo(data.search || null);
    } catch (e) { setError(e.message); setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [authFetch, effectiveTree, sort, page, pageSize, debouncedQuery]);
  useEffect(() => { load(); }, [load]);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !openId) load();
    wasOpen.current = !!openId;
  }, [openId, load]);
  useEffect(() => { setPage(0); }, [debouncedQuery, pageSize, filterTree]);

  const loadSavedViews = useCallback(async () => {
    try { setSavedViews(await authFetch('/api/saved-filters/clients')); } catch (e) { /* non-fatal */ }
  }, [authFetch]);
  const loadDistincts = useCallback(async () => {
    const keys = [...new Set((CLIENT_SCHEMA.fields || []).filter(f => f.dropdown).map(f => f.dropdown))];
    const out = {};
    await Promise.all(keys.map(async k => {
      try { out[k] = await authFetch('/api/filter/clients/distinct/' + encodeURIComponent(k)); } catch (e) { /* skip */ }
    }));
    setDistincts(out);
  }, [authFetch]);
  useEffect(() => { loadSavedViews(); loadDistincts(); }, [loadSavedViews, loadDistincts]);

  function flash(msg) { setToast(msg); setTimeout(() => setToast(null), 2600); }
  // The import engine puts its plain-English reason in body.message.
  const importFetch = useCallback(async (p, o) => {
    try { return await authFetch(p, o); } catch (e) { if (e.body && e.body.message) e.message = e.body.message; throw e; }
  }, [authFetch]);

  async function saveCurrentView(name, shared) {
    try { await authFetch('/api/saved-filters/clients', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, tree: effectiveTree() || filterTree, shared: !!shared }) }); loadSavedViews(); }
    catch (e) { flash(e.message); }
  }
  async function deleteView(viewId) {
    try { await authFetch('/api/saved-filters/' + viewId, { method: 'DELETE' }); if (activeViewId === viewId) { setActiveViewId(null); setFilterTree(null); } loadSavedViews(); }
    catch (e) { flash(e.message); }
  }

  async function createClient() {
    if (!nf.name.trim()) { flash('Name is required'); return; }
    setCreating(true);
    try {
      const created = await authFetch('/api/clients', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(nf) });
      setShowNew(false);
      setNf({ name: '', website: '', sector: '', size: '', location: '', status: 'prospect', brief: '' });
      await load();
      navigate(paths.client(created.ref || created.id));
    } catch (e) {
      if (e.status === 409 && e.body && e.body.existing) {
        flash('That client already exists — opening it');
        setShowNew(false);
        navigate(paths.client(e.body.existing.ref || e.body.existing.id));
      } else { flash(e.message); }
    }
    finally { setCreating(false); }
  }

  const toggleRow = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelectedIds(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));

  // C1-NEWS: every contact of the ticked clients, or of every client in this view, onto the Insights email list.
  // A dry run first says how many are new; people who left before are never put back from here.
  const [insBusy, setInsBusy] = useState(false);
  async function addClientsToInsights(all) {
    const scope = all ? { filter: effectiveTree() || { and: [] } } : { client_ids: Array.from(selectedIds) };
    const call = (extra) => authFetch('/api/newsletter/from-clients', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(Object.assign({}, scope, extra)) });
    setInsBusy(true);
    try {
      const dry = await call({ dry_run: true });
      const left = (dry.left_unsubscribed || 0) + (dry.left_bounced || 0) + (dry.left_complained || 0);
      if (!dry.added) { flash(dry.contacts ? 'Nobody new to add. ' + dry.already + ' already on Insights' + (left ? ', ' + left + ' left before' : '') + (dry.no_email ? ', ' + dry.no_email + ' with no email' : '') + '.' : 'These clients have no contacts yet.'); return; }
      const lines = [dry.added.toLocaleString('en-GB') + (dry.added === 1 ? ' person' : ' people') + ' from ' + dry.clients.toLocaleString('en-GB') + (dry.clients === 1 ? ' client' : ' clients') + ' join the Insights email list and get the next article when it goes live.'];
      const skipped = [dry.already ? dry.already + ' already on it' : '', left ? left + ' left out because they unsubscribed, bounced or marked an email as spam' : '', dry.no_email ? dry.no_email + ' with no email' : ''].filter(Boolean);
      if (skipped.length) lines.push('Not added: ' + skipped.join(', ') + '.');
      if (!(await gtConfirm({ title: 'Add ' + dry.added.toLocaleString('en-GB') + ' to Insights?', message: lines, confirmLabel: 'Add them' }))) return;
      const r = await call({});
      flash(r.added.toLocaleString('en-GB') + ' added to Insights');
      setSelectedIds(new Set());
    } catch (e) { flash(e.message); } finally { setInsBusy(false); }
  }

  function onSort(field) { setSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }); }
  function applyColumns() { setVisibleKeys(draftKeys); setShowColumns(false); }
  const columns = visibleKeys.map(k => ALL_CLIENT_COLUMNS.find(c => c.key === k)).filter(Boolean);

  const exportCsv = () => {
    const cols = columns;
    const header = cols.map(c => c.label).join(',');
    const lines = rows.map(r => cols.map(c => { const v = r[c.key]; const s = v == null ? '' : String(v).replace(/"/g, '""'); return /[",\n]/.test(s) ? `"${s}"` : s; }).join(','));
    const csv = [header, ...lines].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  if (openId) return <GtClientRecord clientId={openId} initialRoleId={roleId || null} getToken={getToken} userName={userName} userImage={userImage}
    onBack={() => navigate(paths.clients())}
    initialCandidateId={candidateId || null}
    onOpenRole={(rid) => navigate(paths.clientRole(openId, rid))}
    onCloseRole={() => navigate(paths.client(openId))}
    onOpenCandidate={(cid) => navigate(paths.clientRoleCandidate(openId, roleId, cid))}
    onCloseCandidate={() => navigate(paths.clientRole(openId, roleId))} />;

  return (
    <div className="gtc-wrap">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Clients</h1>
          <p className="gtc-sub">{total} client{total === 1 ? '' : 's'}{filterActive || debouncedQuery ? ' matching' : ''}</p>
        </div>
        <div className="gtc-head-right">
          <button className="gtc-btn-ghost" type="button" onClick={() => setImporting(true)}>Import CSV</button>
          <button className="gtc-primary" type="button" onClick={() => setShowNew(true)}>Add client</button>
        </div>
      </div>

      <div className="gtc-tabs">
        <button type="button" className={'gtc-tab' + (activeViewId === null ? ' gtc-tab--on' : '')}
          onClick={() => { setActiveViewId(null); setFilterTree(null); setPage(0); }}>All clients</button>
        {savedViews.map(v => (
          <button key={v.id} type="button" className={'gtc-tab' + (activeViewId === v.id ? ' gtc-tab--on' : '')}
            onClick={() => { setActiveViewId(v.id); applyViewTree(v.tree); setPage(0); }}>{v.name}</button>
        ))}
        <button type="button" className="gtc-tab gtc-tab--add" title="New view" onClick={() => setShowFilters(true)}>+</button>
      </div>

      <div className="gtc-toolbar">
        <div className="gtc-tb-search">
          <span className="gtc-tb-searchicon">⌕</span>
          <input placeholder="Search clients: name, sector, tech, notes…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <div className="gtc-tb-actions">
          <button className={'gtc-tb-btn' + (showColumns ? ' gtc-tb-btn--on' : '')} type="button" onClick={() => setShowColumns(v => !v)}><span className="gtc-tb-ic">▦</span>Columns</button>
          <button className={'gtc-tb-btn' + (filterActive ? ' gtc-tb-btn--on' : '')} type="button" onClick={() => setShowFilters(v => !v)}>
            <span className="gtc-tb-ic">⛃</span>Filters{filterActive ? ` · ${filterTree.and.length}` : ''}
          </button>
          <button className="gtc-tb-btn" type="button" title="Export current view to CSV" onClick={exportCsv}><span className="gtc-tb-ic">↧</span>Export</button>
        </div>
      </div>

      <SearchHint info={searchInfo} query={debouncedQuery} mode={searchMode} onMode={setSearchMode}
        onQuery={(t) => { setQuery(t); setDebouncedQuery(t); setSearchMode('auto'); }} /> {/* C1-AISEARCH */}

      {selectedIds.size > 0 && (
        <div className="gtnl-selbar">{/* C1-NEWS */}
          <b>{selectedIds.size} selected</b>
          <button type="button" className="gtc-btn-ghost" disabled={insBusy} onClick={() => addClientsToInsights(false)}>{insBusy ? 'Checking…' : 'Add their contacts to Insights'}</button>
          {total > selectedIds.size && <button type="button" className="gtc-btn-ghost" disabled={insBusy} onClick={() => addClientsToInsights(true)}>Or every {filterActive || debouncedQuery ? 'matching ' : ''}client: {total.toLocaleString('en-GB')}</button>}
          <button type="button" className="gtnl-selbar-x" onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}

      {showColumns && (
        <div className="gtc-colpanel">
          <div className="gtc-colpanel-head">
            <span>Edit columns</span>
            <button type="button" className="gtc-colpanel-reset" onClick={() => setDraftKeys(CLIENT_COLUMNS.map(c => c.key))}>Reset to default</button>
          </div>
          <div className="gtc-colpanel-cols">
            <div className="gtc-colpanel-visible">
              <div className="gtc-colpanel-subhead">Visible — drag to reorder</div>
              {draftKeys.map(k => {
                const col = ALL_CLIENT_COLUMNS.find(c => c.key === k);
                if (!col) return null;
                const locked = col.key === 'id' || col.key === 'name';
                return (
                  <div key={col.key}
                    className={'gtc-colrow' + (locked ? ' gtc-colrow--locked' : '') + (dragKey === col.key ? ' gtc-colrow--dragging' : '')}
                    draggable={!locked}
                    onDragStart={() => !locked && setDragKey(col.key)}
                    onDragEnd={() => setDragKey(null)}
                    onDragOver={e => {
                      e.preventDefault();
                      if (locked || !dragKey || dragKey === col.key) return;
                      setDraftKeys(prev => { const next = prev.filter(x => x !== dragKey); const idx = next.indexOf(col.key); next.splice(idx, 0, dragKey); return next; });
                    }}>
                    <span className="gtc-colgrip">{locked ? '🔒' : '⠿'}</span>
                    <span className="gtc-collabel">{col.label}</span>
                    {!locked && <button type="button" className="gtc-colhide" onClick={() => setDraftKeys(prev => prev.filter(x => x !== col.key))}>Hide</button>}
                  </div>
                );
              })}
            </div>
            <div className="gtc-colpanel-hidden">
              <div className="gtc-colpanel-subhead">Hidden — click to add</div>
              {ALL_CLIENT_COLUMNS.filter(c => !draftKeys.includes(c.key)).map(col => (
                <button key={col.key} type="button" className="gtc-coladd" onClick={() => setDraftKeys(prev => [...prev, col.key])}>+ {col.label}</button>
              ))}
            </div>
          </div>
          <div className="gtc-colpanel-foot">
            <button type="button" className="gtc-btn-ghost" onClick={() => setShowColumns(false)}>Cancel</button>
            <button type="button" className="gtc-primary" onClick={applyColumns}>Apply</button>
          </div>
        </div>
      )}

      <div className="gtc-body">
        {showFilters && (
          <GtFilterRail
            schema={CLIENT_SCHEMA}
            value={filterTree}
            onChange={setFilterTree}
            matchCount={total}
            loading={loading}
            savedViews={savedViews}
            onSaveView={saveCurrentView}
            onApplyView={(v) => { setActiveViewId(v.id); applyViewTree(v.tree); }}
            onDeleteView={deleteView}
            distincts={distincts}
          />
        )}
        <div className="gtc-main">
          {error && <div className="gtc-error">{error}</div>}
          <GtTable
            columns={columns}
            rows={rows}
            loading={loading}
            selectedIds={selectedIds}
            onToggle={toggleRow}
            onToggleAll={toggleAll}
            onCandidateClick={(row) => navigate(paths.client(row.ref || row.id))}
            sort={sort}
            onSort={onSort}
          />
          <GtPager page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />
        </div>
      </div>

      {showNew && (
        <div className="gtc-modal-backdrop" onClick={() => !creating && setShowNew(false)}>
          <div className="gtc-modal" onClick={e => e.stopPropagation()}>
            <div className="gtc-modal-head">Add client</div>
            <div className="gtc-modal-body">
              <label className="gtc-field"><span>Name</span><input value={nf.name} onChange={e => setNf({ ...nf, name: e.target.value })} placeholder="Nimbus Labs" /></label>
              <label className="gtc-field"><span>Website</span><input value={nf.website} onChange={e => setNf({ ...nf, website: e.target.value })} placeholder="nimbus.ai" /></label>
              <label className="gtc-field"><span>Sector</span>
                <GtCombo
                  value={nf.sector}
                  options={sectors}
                  onChange={v => setNf({ ...nf, sector: v })}
                  placeholder="Select a sector"
                  allowFree
                />
              </label>
              <label className="gtc-field"><span>Size</span><input value={nf.size} onChange={e => setNf({ ...nf, size: e.target.value })} placeholder="10-50" /></label>
              <label className="gtc-field"><span>Location</span><input value={nf.location} onChange={e => setNf({ ...nf, location: e.target.value })} placeholder="London" /></label>
              <label className="gtc-field"><span>Status</span><GtSelect value={nf.status} options={STATUS_OPTIONS} onChange={v => setNf({ ...nf, status: v })} /></label>
              <label className="gtc-field"><span>Brief (optional context on the buyer)</span><textarea rows={4} value={nf.brief} onChange={e => setNf({ ...nf, brief: e.target.value })} placeholder="What they do, who we deal with, anything worth remembering." /></label>
            </div>
            <div className="gtc-modal-foot">
              <button type="button" className="gtc-btn-ghost" disabled={creating} onClick={() => setShowNew(false)}>Cancel</button>
              <button type="button" className="gtc-primary" disabled={creating} onClick={createClient}>{creating ? 'Creating…' : 'Create client'}</button>
            </div>
          </div>
        </div>
      )}

      {importing && (
        <GtImportWizard entity="clients" authFetch={importFetch} flash={flash}
          onClose={() => setImporting(false)} onDone={() => load()} />
      )}

      {toast && <div className="gtc-toast">{toast}</div>}
    </div>
  );
}
